import React, { useState, useEffect, useCallback, useContext } from "react";
import { Redirect } from "react-router-dom";
import { toast } from "react-toastify";
import {
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Button,
  Chip,
  Typography,
  CircularProgress,
  Tooltip,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import RefreshIcon from "@material-ui/icons/Refresh";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(2) },
  filterBar: { marginBottom: theme.spacing(2) },
  paper: { overflowX: "auto" },
  table: { minWidth: 900 },
  chip: { fontWeight: 600, fontSize: 11 },
  cellTruncate: { maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  loadMore: { textAlign: "center", padding: theme.spacing(2) },
  emptyRow: { textAlign: "center", padding: theme.spacing(4), color: "#aaa" },
}));

const ENTITY_LABELS = {
  ticket: "Ticket",
  user: "Usuário",
  queue: "Fila",
  tag: "Tag",
  contact: "Contato",
  product: "Produto",
  reason: "Motivo",
  setting: "Configuração",
  working_hours: "Horário",
  quick_answer: "Resposta Rápida",
  agendamento: "Agendamento",
  webhook: "Webhook",
};

const ACTION_LABELS = {
  create: "Criação",
  update: "Alteração",
  delete: "Exclusão",
  transfer: "Transferência",
  assign: "Atribuição",
  close: "Fechamento",
  reopen: "Reabertura",
  block: "Bloqueio",
  unblock: "Desbloqueio",
  status_change: "Mudança de Status",
};

const ACTION_COLORS = {
  create: "#2e7d32",
  update: "#1565c0",
  delete: "#c62828",
  transfer: "#6a1b9a",
  assign: "#00695c",
  close: "#e65100",
  reopen: "#2e7d32",
  block: "#b71c1c",
  unblock: "#1b5e20",
  status_change: "#4527a0",
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  return d.toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" });
};

const AuditLogs = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [logs, setLogs] = useState([]);
  const [count, setCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);

  const [filters, setFilters] = useState({
    entityType: "",
    action: "",
    startDate: "",
    endDate: "",
    search: "",
  });

  const [appliedFilters, setAppliedFilters] = useState({ ...filters });

  const fetchLogs = useCallback(async (page, activeFilters) => {
    setLoading(true);
    try {
      const params = { pageNumber: page, ...activeFilters };
      Object.keys(params).forEach((k) => {
        if (!params[k]) delete params[k];
      });
      const { data } = await api.get("/audit-logs", { params });
      if (page === 1) {
        setLogs(data.logs);
      } else {
        setLogs((prev) => [...prev, ...data.logs]);
      }
      setCount(data.count);
      setHasMore(data.hasMore);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLogs(1, appliedFilters);
    setPageNumber(1);
  }, [appliedFilters, fetchLogs]);

  const handleApplyFilters = () => {
    setAppliedFilters({ ...filters });
  };

  const handleClearFilters = () => {
    const empty = { entityType: "", action: "", startDate: "", endDate: "", search: "" };
    setFilters(empty);
    setAppliedFilters(empty);
  };

  const handleLoadMore = () => {
    const next = pageNumber + 1;
    setPageNumber(next);
    fetchLogs(next, appliedFilters);
  };

  if (user?.profile !== "superadmin") {
    return <Redirect to="/tickets" />;
  }

  return (
    <MainContainer>
      <MainHeader>
        <Title>Auditoria</Title>
        <Typography variant="body2" color="textSecondary" style={{ marginLeft: 16 }}>
          {count} registro{count !== 1 ? "s" : ""}
        </Typography>
      </MainHeader>

      <div className={classes.root}>
        <Paper className={classes.filterBar} style={{ padding: 16 }}>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Entidade</InputLabel>
                <Select
                  value={filters.entityType}
                  onChange={(e) => setFilters((f) => ({ ...f, entityType: e.target.value }))}
                >
                  <MenuItem value="">Todas</MenuItem>
                  {Object.entries(ENTITY_LABELS).map(([k, v]) => (
                    <MenuItem key={k} value={k}>{v}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Ação</InputLabel>
                <Select
                  value={filters.action}
                  onChange={(e) => setFilters((f) => ({ ...f, action: e.target.value }))}
                >
                  <MenuItem value="">Todas</MenuItem>
                  {Object.entries(ACTION_LABELS).map(([k, v]) => (
                    <MenuItem key={k} value={k}>{v}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Data inicial"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={filters.startDate}
                onChange={(e) => setFilters((f) => ({ ...f, startDate: e.target.value }))}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Data final"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={filters.endDate}
                onChange={(e) => setFilters((f) => ({ ...f, endDate: e.target.value }))}
              />
            </Grid>

            <Grid item xs={12} sm={8} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Buscar"
                placeholder="Label, usuário, valor..."
                value={filters.search}
                onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
                onKeyPress={(e) => e.key === "Enter" && handleApplyFilters()}
              />
            </Grid>

            <Grid item xs={12} sm={4} md={1}>
              <Grid container spacing={1}>
                <Grid item>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={handleApplyFilters}
                    startIcon={<SearchIcon />}
                  >
                    Filtrar
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleClearFilters}
                    startIcon={<RefreshIcon />}
                  >
                    Limpar
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Paper>

        <Paper className={classes.paper}>
          <Table className={classes.table} size="small">
            <TableHead>
              <TableRow>
                <TableCell>Data/Hora</TableCell>
                <TableCell>Entidade</TableCell>
                <TableCell>Label</TableCell>
                <TableCell>Ação</TableCell>
                <TableCell>Campo</TableCell>
                <TableCell>Antes</TableCell>
                <TableCell>Depois</TableCell>
                <TableCell>Usuário</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={8} className={classes.emptyRow}>
                    Nenhum registro encontrado
                  </TableCell>
                </TableRow>
              )}
              {logs.map((log) => (
                <TableRow key={log.id} hover>
                  <TableCell style={{ whiteSpace: "nowrap", fontSize: 12 }}>
                    {formatDate(log.createdAt)}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={ENTITY_LABELS[log.entityType] || log.entityType}
                      size="small"
                      className={classes.chip}
                      style={{ background: "#e3f2fd", color: "#1565c0" }}
                    />
                  </TableCell>
                  <TableCell className={classes.cellTruncate}>
                    <Tooltip title={log.entityLabel || log.entityId} placement="top">
                      <span>{log.entityLabel || log.entityId}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={ACTION_LABELS[log.action] || log.action}
                      size="small"
                      className={classes.chip}
                      style={{
                        background: (ACTION_COLORS[log.action] || "#555") + "22",
                        color: ACTION_COLORS[log.action] || "#555",
                        border: `1px solid ${ACTION_COLORS[log.action] || "#555"}`,
                      }}
                    />
                  </TableCell>
                  <TableCell style={{ fontSize: 12, color: "#666" }}>
                    {log.field || "-"}
                  </TableCell>
                  <TableCell className={classes.cellTruncate} style={{ color: "#c62828", fontSize: 12 }}>
                    <Tooltip title={log.beforeValue || ""} placement="top">
                      <span>{log.beforeValue || "-"}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell className={classes.cellTruncate} style={{ color: "#2e7d32", fontSize: 12 }}>
                    <Tooltip title={log.afterValue || ""} placement="top">
                      <span>{log.afterValue || "-"}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell style={{ fontSize: 12 }}>
                    {log.userName || log.user?.name || "-"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {loading && (
            <div className={classes.loadMore}>
              <CircularProgress size={24} />
            </div>
          )}

          {hasMore && !loading && (
            <div className={classes.loadMore}>
              <Button onClick={handleLoadMore} color="primary">
                Carregar mais ({count - logs.length} restantes)
              </Button>
            </div>
          )}
        </Paper>
      </div>
    </MainContainer>
  );
};

export default AuditLogs;
